import { SelectionModel } from '@angular/cdk/collections';
import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, Inject, OnDestroy, WritableSignal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatFabButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { IndustryCatalogType, Pagination } from '@core/shared/domain';
import {
  BasicFlyoutComponent,
  CatalogTypeIconComponent,
  FilterSelectionOption,
  FlyoutAnimation,
  FlyoutComponent,
  InfiniteScrollComponent,
  MultiSelectionGroupComponent,
  OVERLAY_DATA,
  PagedDataSource,
  SearchFieldComponent,
  SelectAllFlyoutElementsButtonComponent,
  SelectorItemComponent,
} from '@core/ui';
import { FlyoutIndustryCatalog } from '@mp/content-manager/plattformkataloge/data-access';

import { CATALOG_TYPE_FILTER_OPTIONS } from '../catalog-type-filter-options';

import { IsSelectedByIdPipe } from './is-selected-by-id.pipe';
import { KatalogeQuerier } from './kataloge-querier';

type FetcherParams = { searchTerm: string; catalogType: Array<IndustryCatalogType> };
type ReturnType = Array<FlyoutIndustryCatalog> | undefined;
type InputType = { querier: KatalogeQuerier; excludeIds?: Array<string> };

@Component({
  selector: 'mp-kataloge-flyout',
  templateUrl: './kataloge-flyout.component.html',
  styleUrl: './kataloge-flyout.component.scss',
  animations: FlyoutAnimation.Named('openClose'),
  host: {
    '[@openClose]': 'animationState',
  },
  imports: [
    AsyncPipe,

    MatIcon,
    MatFabButton,

    BasicFlyoutComponent,
    SelectAllFlyoutElementsButtonComponent,
    SearchFieldComponent,
    MultiSelectionGroupComponent,
    InfiniteScrollComponent,
    SelectorItemComponent,
    IsSelectedByIdPipe,
    CatalogTypeIconComponent,
  ],
})
export class KatalogeFlyoutComponent implements FlyoutComponent<ReturnType>, OnDestroy {
  readonly animationState: WritableSignal<FlyoutAnimation.State> = signal<FlyoutAnimation.State>('open');

  readonly excludeIds: Array<string>;

  readonly querier: InputType['querier'];

  readonly dataSource: PagedDataSource<FlyoutIndustryCatalog, FetcherParams>;

  private readonly selectionModel = new SelectionModel<FlyoutIndustryCatalog>(true);
  get selection() {
    return this.selectionModel.selected;
  }

  private readonly _afterClosed$ = new Subject<ReturnType>();
  readonly afterClosed$ = this._afterClosed$.asObservable();

  protected readonly catalogTypeFilterOptions: FilterSelectionOption<IndustryCatalogType>[] =
    CATALOG_TYPE_FILTER_OPTIONS;

  constructor(
    @Inject(OVERLAY_DATA) payload: InputType,
    private readonly destroyRef: DestroyRef,
  ) {
    this.querier = payload.querier;
    this.excludeIds = payload.excludeIds || [];

    this.dataSource = this.buildDataSource();
  }

  private buildDataSource(): PagedDataSource<FlyoutIndustryCatalog, FetcherParams> {
    const initialParams = { searchTerm: '', catalogType: [] };
    const fetcher = (pagination: Pagination, params: FetcherParams) =>
      this.querier.getPage(pagination, params).pipe(
        map(({ data, pagination }) => ({
          results: data,
          hasMore: pagination.currentPage < pagination.totalPageCount,
        })),
      );

    return new PagedDataSource<FlyoutIndustryCatalog, FetcherParams>(fetcher, initialParams);
  }

  handleFilterChange({ catalogType }: { catalogType?: Array<IndustryCatalogType> }): void {
    this.dataSource.updateQueryParams({ catalogType });
  }

  handleSearch(searchTerm: string): void {
    this.dataSource.updateQueryParams({ searchTerm });
  }

  toggleSelect(item: FlyoutIndustryCatalog): void {
    const selectedKatalog = this.selectionModel.selected.find((katalog) => katalog.catalogId === item.catalogId);
    selectedKatalog ? this.selectionModel.deselect(selectedKatalog) : this.selectionModel.select(item);
  }

  selectAll(): void {
    // NOTE: This Implementation fetches ALL data from the backend (no Pagination).
    //       This does NOT select the currently selected page, but ALL pages.

    this.querier
      .getAllKataloge(this.dataSource.queryParams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (kataloge) => {
          // NOTE: If items are manually selected first, then via "Select All", the WHOLE
          //       list is added ON TOP of the manually selected items. To ensure a proper display of the
          //       "Deselect all" button, we need to clear the SelectionModel before we add all available items

          this.selectionModel.clear();
          this.selectionModel.select(...kataloge);
        },
      });
  }

  deselectAll(): void {
    this.selectionModel.clear();
  }

  cancel(): void {
    this.close();
  }

  confirm(): void {
    this.close(this.selection);
  }

  close(returnValue?: ReturnType): void {
    this.animationState.set('closed');

    this._afterClosed$.next(returnValue);
    this._afterClosed$.complete();
  }

  ngOnDestroy(): void {
    this._afterClosed$.complete();
  }
}
